<template>
    <div class="d-flex flex-column">
        <v-row class="mx-0 justify-space-between mt-6 mb-3">
            <v-col cols="12" md="5" class="px-0 d-flex align-center">
                <v-autocomplete
                    v-model="filters.job_title_code"
                    label="Função"
                    :items="jobTitleOptions"
                    item-title="label"
                    item-value="code"
                    :loading="isLoadingJobTitleOptions"
                    :disabled="isLoadingJobTitleOptions"
                    clearable
                    disabled
                    hide-details
                    @update:model-value="filterByJobTitle"
                />
            </v-col>
            <v-col cols="12" md="auto" class="px-0">
                <v-btn prepend-icon="mdi-plus" color="primary" class="mt-4 mb-6" disabled>
                    Criar Modelo
                </v-btn>
            </v-col>
        </v-row>

        <v-data-table-server
            v-model:items-per-page="filters.per_page"
            v-model:page="filters.page"
            :headers="tableHeaders"
            :items="assessmentModelJobTitles"
            :items-length="filters.total ?? 0"
            :loading="isLoading"
            :loading-text="firstLoad ? 'Obtendo lista de modelos' : 'Atualizando lista de modelos'"
            item-value="id"
            :no-data-text="hasFailed ? 'Ocorreu um erro a obter a lista de modelos' : 'Não foram encontrados modelos de acordo com os parâmetros de pesquisa.'"
            :mobile="mdAndDown"
            hide-default-footer
            @update:sort-by="handleSortingUpdate"
        >
            <template #item.company_name="{ item }">
                {{ item.company?.name ?? "—" }}
            </template>
            <template #item.job_title_description="{ item }">
                {{ jobTitleLabel(item.job_title) }}
            </template>
            <template #item.self_assessment_enabled="{ item }">
                <v-chip
                    :color="selfAssessmentColor(item.self_assessment_enabled)"
                    :prepend-icon="selfAssessmentIcon(item.self_assessment_enabled)"
                    variant="tonal"
                >
                    {{ selfAssessmentLabel(item.self_assessment_enabled) }}
                </v-chip>
            </template>
            <template #item.actions="{ item }">
                <div class="d-flex flex-column flex-md-row justify-lg-end ga-2 align-center">
                    <v-tooltip
                        v-if="item?.can_get"
                        text="Ver detalhes" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                icon="mdi-eye"
                                color="primary"
                                rounded="xl"
                                size="x-small"
                                @click="openDialogForAction('get', null, item?.id)"
                            />
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_get"
                        text="Ver detalhes (nova aba/página)" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                icon="mdi-open-in-new"
                                color="primary"
                                rounded="xl"
                                size="x-small"
                                variant="outlined"
                                @click="openDetailsInNewTab(item)"
                            />
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_update"
                        text="Editar" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                icon="mdi-pencil"
                                color="info"
                                rounded="xl"
                                size="x-small"
                                
                            />
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_update"
                        text="Editar (nova aba/página)" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                icon="mdi-pencil-box-multiple"
                                color="info"
                                rounded="xl"
                                size="x-small"
                                variant="outlined"
                            />
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_delete"
                        text="Apagar" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                icon="mdi-trash-can"
                                color="error"
                                rounded="xl"
                                size="x-small"
                                
                            />
                        </template>
                    </v-tooltip>
                </div>
            </template>
        </v-data-table-server>

        <div class="d-flex justify-end align-center">
            <strong>{{ filters.from ?? 0 }} - {{ filters.to ?? '??' }} / {{ filters.total ?? '??' }}</strong>
            <v-pagination
                v-model="filters.page"
                :length="filters.last_page ?? 1"
                :total-visible="0"
                @update:model-value="fetchAssessmentModels"
            />
        </div>

        
    </div>
</template>

<script setup>
import {
    onMounted,
    ref,
} from "vue";
import {
    useRouter,
} from "vue-router";
import {
    useDisplay,
} from "vuetify";
import {useDialog} from "@internal_projects_dd/universal-dialog";
import manipulators from "@/composables/stringManipulations.js"

import AssessmentModels from "@/api/assessments/models/AssessmentModels";
import AssessmentModelForm from "./AssessmentModelForm.vue";

const assessmentModelJobTitles = ref([]);
const firstLoad = ref(true);
const hasFailed = ref(false);
const isDetailsDialogOpen = ref(false);
const isLoading = ref(false);
const isLoadingJobTitleOptions = ref(false);
const jobTitleOptions = ref([]);
const selectedAssessmentModelJobTitleId = ref(null);

const dialog = useDialog();

const filters = ref({
    direction: "asc",
    from: 1,
    job_title_code: null,
    last_page: 1,
    order_by: "model_code",
    page: 1,
    per_page: 10,
    to: null,
    total: null,
});

const tableHeaders = [
    {
        title: "Código do Modelo",
        align: "start",
        sortable: true,
        key: "model_code",
    },
    {
        title: "Empresa",
        align: "start",
        sortable: true,
        key: "company_name",
    },
    {
        title: "Função",
        align: "start",
        sortable: true,
        key: "job_title_description",
    },
    {
        title: "Autoavaliação",
        align: "center",
        sortable: false,
        key: "self_assessment_enabled",
    },
    {
        title: "Acções",
        align: "end",
        sortable: false,
        key: "actions",
    },
];

const { mdAndDown } = useDisplay();
const router = useRouter();

onMounted(async () => {
    await Promise.all([
        fetchAssessmentModels(),
        // fetchJobTitleOptions(),
    ]);
});

async function fetchAssessmentModels() {
    isLoading.value = true;
    hasFailed.value = false;

    try {
        const response = await AssessmentModels.all(filters.value);
        const pagination = response?.data;

        assessmentModelJobTitles.value = Array.isArray(pagination?.data)
            ? pagination.data
            : [];

        filters.value = {
            ...filters.value,
            from: pagination?.from ?? 0,
            last_page: pagination?.last_page ?? 1,
            page: pagination?.current_page ?? filters.value.page,
            to: pagination?.to ?? 0,
            total: pagination?.total ?? 0,
        };
    } catch (error) {
        assessmentModelJobTitles.value = [];
        hasFailed.value = true;
        console.error("Erro ao obter a lista de modelos de avaliação:", error);
    } finally {
        firstLoad.value = false;
        isLoading.value = false;
    }
}

async function fetchJobTitleOptions() {
    isLoadingJobTitleOptions.value = true;

    try {
        const response = await AssessmentModels.jobTitles();

        jobTitleOptions.value = (response?.data?.data ?? response?.data ?? [])?.map((jobTitle) => ({
            ...jobTitle,
            label: jobTitle.description
                ? `${jobTitle.code} - ${jobTitle.description}`
                : jobTitle.code,
        }));
    } catch (error) {
        jobTitleOptions.value = [];
        console.error("Erro ao obter a lista de funções:", error);
    } finally {
        isLoadingJobTitleOptions.value = false;
    }
}

function filterByJobTitle() {
    filters.value.page = 1;
    fetchAssessmentModels();
}

function handleSortingUpdate(updatedSorting = []) {
    const sorting = Array.isArray(updatedSorting)
        ? updatedSorting[0]
        : updatedSorting;

    if (!sorting?.key || !sorting?.order) {
        return;
    }

    filters.value.order_by = sorting.key;
    filters.value.direction = sorting.order;
    filters.value.page = 1;
    fetchAssessmentModels();
}

function openDetailsDialog(item) {
    if (!item?.can_get) {
        return;
    }

    selectedAssessmentModelJobTitleId.value = item.id;
    isDetailsDialogOpen.value = true;
}

function openDetailsInNewTab(item) {
    if (!item?.can_get) {
        return;
    }

    const route = router.resolve({
        name: "assessment-model-details",
        params: {
            id: item.id,
        },
    });

    window.open(route.href, "_blank", "noopener");
}

function jobTitleLabel(jobTitle) {
    const code = jobTitle?.code;
    const description = jobTitle?.description;

    if (code && description) {
        return `${code} - ${description}`;
    }

    return code || description || "—";
}

function selfAssessmentColor(value) {

    switch (value) {
        case true:
		case "1":
            return "success";
            
        case null:
            return "default";
    
        case "0":
        default:
            return "error";
    }
}

function selfAssessmentIcon(value) {

    switch (value) {
        case true:
		case "1":
            return "mdi-check";
            
        case null:
            return "mdi-minus";
    
        case "0":
        default:
            return "mdi-close";
    }
}

function selfAssessmentLabel(value) {

    switch (value) {
        case true:
		case "1":
            return "Sim";
            
        case null:
            return "N/A";
    
        case "0":
        default:
            return "Não";
    }
}

const openDialogForAction = async (action = null, model = null, model_id = null) => {
    
    if(!action) {
        return;
    }

    const idempotency_key = self?.crypto?.randomUUID() ?? null;

    const componentProperties = resolveComponentPropertiesForAction(action, model, model_id);
    const actionSpecificDialogOptions = resolveDialogOptionsForAction(action);

    componentProperties.idempotency_key = idempotency_key;
    componentProperties.onSubmit = createAssessmentModelSubmitHandler(action);

    await dialog.open({
        component: AssessmentModelForm,

        props: {
            ...componentProperties
        },
        options: {
            ...actionSpecificDialogOptions
        }
    });
}

async function openSuccessDialog(bodyText) {
    await dialog.open({
        options: {
            bodyText,
            persistent: true,
            maxWidth: 500,
            showCancel: false,
            confirmActionIcon: "",
            confirmActionText: "Ok",
            confirmActionColor: "primary",
        },
    });
}

function resolveComponentPropertiesForAction(action = null, model = null, model_id = null) {
    return {
        action,
        model,
        modelId: model?.id ?? model_id ?? null,
    };
}


function createAssessmentModelSubmitHandler(action) {
    return null;
}

function resolveDialogOptionsForAction(action) {

    const dialogOptions = {
        titleBackgroundColor: "transparent",
        titleTextColor: "primary",
        persistent: true,
        maxWidth: "640",
        showCancel: false,
        onConfirm: "submit",
        confirmActionColor: "primary",
        onConfirmFailureExpectedResult: false,
        onConfirmFailureThrow: true,

        
    };

    let uiActionVerb;
    let uiActionAdjective;
    let uiActionUnderWayLabel;
    let apiMethod;

    switch (action) {

        case "create":
        case "new":

            uiActionVerb = "criar";
            uiActionAdjective = "criado";
            uiActionUnderWayLabel = "criando";

            dialogOptions.confirmActionIcon = "mdi-plus";

            apiMethod = "create";

            break;
            
        case "read":
        case "get":

            dialogOptions.onConfirm = null;
            dialogOptions.confirmActionIcon = "mdi-close";
            dialogOptions.persistent = false;
            dialogOptions.title = "Detalhes do Modelo";
            dialogOptions.confirmActionText = "Fechar"
            dialogOptions.onConfirmActionText = "Fechando";

            dialogOptions.onConfirm = async (readDialog) => {
                return true;
            }
            
            break;
        
        case "edit":
        case "update":

            uiActionVerb = "atualizar";
            uiActionAdjective = "atualizado";
            uiActionUnderWayLabel = "atualizando";

            dialogOptions.confirmActionIcon = "mdi-content-save";

            apiMethod = "update";
            
            break;

        case "delete":
        case "remove":

            uiActionVerb = "apagar";
            uiActionAdjective = "apagado";
            uiActionUnderWayLabel = "apagando";

            dialogOptions.confirmActionIcon = "mdi-delete";
            dialogOptions.confirmActionColor = "error";

            apiMethod = "delete";

            break;
    
        default:
            throw new RangeError(
                `Invalid action: '${action}'!`
            );
    }

    // returning early in read action since it does not need any 
    // further options
    if (["read", "get"].includes(action)) {
        return dialogOptions;
    }

    // set shared dynamic properties
    dialogOptions.title = `${manipulators.firstLetterToUpperCase(uiActionVerb)} Modelo`;
    dialogOptions.confirmActionText = `${manipulators.firstLetterToUpperCase(uiActionVerb)} modelo`;
    dialogOptions.onConfirmActionText = `${manipulators.firstLetterToUpperCase(uiActionUnderWayLabel)} modelo...`;
    
    dialogOptions.onConfirmSuccess = async (result) => {
        fetchAbsenceRequests();

        await openSuccessDialog(
            `Modelo ${uiActionAdjective} com sucesso!`
        );

        return result;
    }

    dialogOptions.onConfirmError = async (error, currentDialog) => {
        console.error(
            `Erro a ${uiActionVerb} modelo:`,
            String(error)
        );

        currentDialog.options.onConfirmFailedErrorTitle = 
                    error?.reason
                    ?? `Erro a ${uiActionVerb} modelo:`;

                currentDialog.options.onConfirmFailedErrorMessage = 
                    (Object.values(error?.response?.data?.errors ?? {})?.flat().join("\n")
                    || error?.response?.data?.message)
                    ?? error?.message
                    ?? String(error)
                    ?? `Não foi possível ${uiActionVerb} o modelo! Tente novamente`;
    }


    return dialogOptions;
}

</script>
